import { ClientFolderId, getUserMailboxInfo } from 'owa-client-ids';
import {
    getCalendarFolderId,
    getDefaultCalendar,
    Calendar,
    isWritableCalendar,
} from 'owa-calendar-cache';
import getSelectedCalendarsForUser from './getSelectedCalendarsForUser';
import { getDefaultConnectedAccount } from 'owa-accounts-store';

/**
 * Returns the calendar folder Id (Parent folder Id for a CalendarEvent) that needs to be selected by default in the selector
 */
export default function initializeCalendarFolderId(): ClientFolderId {
    const primaryDefaultCalendar = getDefaultCalendar();
    const primaryUserId = primaryDefaultCalendar?.id.mailboxInfo.userIdentity;

    const primaryCalendarFolderId = getDefaultFolderIdFromSelected(primaryUserId);
    // Check if there are any valid folder ids in primary account, return if it exists
    if (primaryCalendarFolderId) {
        return primaryCalendarFolderId;
    }

    // Use the first element of connected accounts array since we only support one connected account right now
    const connectedAccount = getDefaultConnectedAccount();

    if (connectedAccount) {
        const connectedAccountUserId = connectedAccount.userIdentity;

        const connectedAccountFolderId = getDefaultFolderIdFromSelected(connectedAccountUserId);
        // Check if there are any valid folder ids in connected account, return if it exists
        if (connectedAccountFolderId) {
            return connectedAccountFolderId;
        }
    }

    // in all other cases, return primary account default calendar
    let defaultCalendarFolderId = null;
    let mailboxInfo = getUserMailboxInfo();

    if (primaryDefaultCalendar) {
        mailboxInfo = primaryDefaultCalendar.id.mailboxInfo;
        defaultCalendarFolderId = getCalendarFolderId(primaryDefaultCalendar);
    }

    return {
        Id: defaultCalendarFolderId,
        mailboxInfo: mailboxInfo,
    };
}

/**
 * Gets the default folder id to use in new event compose from a list of selected writable
 * calendars from a given default calendar's primary account.
 * @param selectedCalendars list of selected writable calendars to choose from
 * @param defaultCalendar default calendar from the list provided
 */
function getDefaultFolderIdFromSelected(userIdentity: string) {
    const defaultCalendar = getDefaultCalendar(userIdentity);
    const selectedCalendars: Calendar[] = defaultCalendar
        ? getSelectedWritableCalendars(defaultCalendar.id.mailboxInfo.userIdentity)
        : null;

    const numberCalendars = selectedCalendars ? selectedCalendars.length : 0;
    let selectedCalendar = defaultCalendar;

    if (selectedCalendars && numberCalendars > 0) {
        // Select the last calendar because it is the calendar that was most recently selected by the user
        selectedCalendar = selectedCalendars[numberCalendars - 1];
        // Check to see if the default calendar is in selected list, if so use that
        if (defaultCalendar) {
            selectedCalendars.forEach(calendar => {
                if (calendar.id.id == defaultCalendar.id.id) {
                    selectedCalendar = defaultCalendar;
                }
            });
        }
        return {
            Id: getCalendarFolderId(selectedCalendar),
            mailboxInfo: selectedCalendar.id.mailboxInfo,
        };
    }
    return null;
}

/**
 * Get the list of writable calendars from the selected calendars.
 * @param mailboxId mailbox id
 */
function getSelectedWritableCalendars(mailboxId: string): Calendar[] {
    const selectedCalendars = getSelectedCalendarsForUser(mailboxId);

    return selectedCalendars ? selectedCalendars.filter(isWritableCalendar) : null;
}
